import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/jobsiq/Navbar";
import Footer from "../components/jobsiq/Footer";
import {
    Brain,
    ChatCircleDots,
    VideoCamera,
    Target,
    ArrowRight,
    Sparkle,
    Buildings,
    Briefcase,
    Lightning,
    CheckCircle,
} from "@phosphor-icons/react";
import MatchBar from "../components/jobsiq/MatchBar";

export default function Landing() {
    return (
        <div className="min-h-screen bg-white" data-testid="landing-page">
            <Navbar />

            {/* HERO (dark, full bleed) */}
            <section className="relative bg-slate-950 text-white overflow-hidden">
                <div
                    className="absolute inset-0 opacity-40"
                    style={{
                        backgroundImage:
                            "url('https://images.unsplash.com/photo-1772001936267-b6058748eff4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBnbGFzcyUyMG9mZmljZSUyMGJ1aWxkaW5nJTIwaW50ZXJpb3IlMjBjb3Jwb3JhdGV8ZW58MHx8fHwxNzc4MDI0Nzk1fDA&ixlib=rb-4.1.0&q=85')",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
                <div className="absolute inset-0 bg-slate-950/50" />
                <div className="absolute inset-0 grid-lines" />
                <div className="relative max-w-[1400px] mx-auto px-6 py-24 md:py-36 risein">
                    <div className="flex items-center gap-2 font-mono-plex text-xs uppercase tracking-[0.25em] text-blue-400 mb-6">
                        <Sparkle size={14} weight="fill" />
                        Deep-learning hiring intelligence
                    </div>
                    <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl max-w-4xl leading-[0.95]">
                        Hire on talent intelligence.
                        <br />
                        Not the résumé.
                    </h1>
                    <p className="mt-8 text-lg text-slate-300 max-w-2xl leading-relaxed">
                        Jobs IQ reads between the lines. Our matching model, skill-based
                        chatbots and video-interview analysis surface the candidates who
                        actually belong — not just the ones with the right keywords.
                    </p>
                    <div className="mt-10 flex flex-wrap gap-3">
                        <Link
                            to="/register?role=seeker"
                            data-testid="hero-seeker-cta"
                            className="group px-6 py-3.5 bg-blue-600 hover:bg-white hover:text-slate-950 text-white inline-flex items-center gap-2 transition-colors"
                        >
                            <Briefcase size={18} weight="bold" />
                            I'm looking for work
                            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                        <Link
                            to="/register?role=company"
                            data-testid="hero-company-cta"
                            className="group px-6 py-3.5 border border-white/40 hover:border-white hover:bg-white hover:text-slate-950 inline-flex items-center gap-2 transition-colors"
                        >
                            <Buildings size={18} weight="bold" />
                            I'm hiring talent
                            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </div>

                    <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl font-mono-plex text-sm">
                        {[
                            { k: "92%", v: "match accuracy" },
                            { k: "4.8x", v: "faster shortlists" },
                            { k: "18K", v: "verified roles" },
                            { k: "1.2M", v: "skills indexed" },
                        ].map((s) => (
                            <div key={s.v} className="border-l border-white/20 pl-4">
                                <div className="font-display text-3xl text-white">{s.k}</div>
                                <div className="text-slate-400 text-xs uppercase tracking-widest">{s.v}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Marquee */}
                <div className="relative border-t border-white/10 bg-slate-950 py-4 overflow-hidden">
                    <div className="marquee-track whitespace-nowrap flex gap-16 font-mono-plex text-xs uppercase tracking-[0.3em] text-slate-500">
                        {["a", "b"].map((k) => (
                            <div key={`marquee-${k}`} className="flex gap-16 shrink-0">
                                <span>▸ Deep-matching engine</span>
                                <span>▸ Whisper-powered transcripts</span>
                                <span>▸ Skill-based chatbots</span>
                                <span>▸ Bias-aware ranking</span>
                                <span>▸ Real-time video analysis</span>
                                <span>▸ Role-fit scoring</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FEATURE TETRIS (4 blocks) */}
            <section className="max-w-[1400px] mx-auto px-6 py-24">
                <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
                    <div>
                        <div className="font-mono-plex text-xs uppercase tracking-[0.3em] text-blue-600 mb-3">
                            01 — Platform
                        </div>
                        <h2 className="font-display text-5xl leading-tight max-w-2xl">
                            Four systems, one hiring pipeline.
                        </h2>
                    </div>
                    <p className="text-slate-600 max-w-md">
                        A quiet, opinionated toolkit that replaces guesswork with evidence.
                        Every feature is built for talent intelligence, not spectacle.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200">
                    {/* Big card: matching */}
                    <div className="md:col-span-2 md:row-span-2 bg-white p-10 relative overflow-hidden group">
                        <div className="flex items-center gap-3 font-mono-plex text-xs uppercase tracking-widest text-slate-500">
                            <Brain size={16} weight="duotone" />
                            Deep-learning matching
                        </div>
                        <h3 className="font-display text-4xl mt-4 max-w-lg leading-tight">
                            Skills, experience and cultural fit — quantified.
                        </h3>
                        <p className="text-slate-600 mt-4 max-w-lg">
                            We embed job descriptions, portfolios and conversations into a shared
                            talent-intelligence space, then score candidates on skills,
                            experience <em>and</em> cultural fit — with explainable signals,
                            not a black box.
                        </p>
                        <div className="mt-8 grid grid-cols-2 gap-6 max-w-lg">
                            <div>
                                <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500 mb-2">
                                    Senior Data Engineer · Berlin
                                </div>
                                <MatchBar score={94} size="lg" />
                                <div className="text-xs text-slate-500 mt-2">
                                    Kafka, dbt, Python, Snowflake
                                </div>
                            </div>
                            <div>
                                <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500 mb-2">
                                    Frontend Lead · Remote
                                </div>
                                <MatchBar score={71} size="lg" />
                                <div className="text-xs text-slate-500 mt-2">
                                    React, design systems, a11y
                                </div>
                            </div>
                        </div>
                        <img
                            alt=""
                            src="https://images.unsplash.com/photo-1763929154494-a3d6eba05282?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhYnN0cmFjdCUyMGdlb21ldHJpYyUyMHRlY2hub2xvZ3klMjBwYXR0ZXJuJTIwYmx1ZXxlbnwwfHx8fDE3NzgwMjQ3OTV8MA&ixlib=rb-4.1.0&q=85"
                            className="absolute -right-8 -bottom-8 w-64 h-64 object-cover opacity-40 group-hover:opacity-60 transition-opacity"
                        />
                    </div>

                    {/* Chatbot */}
                    <div className="bg-white p-8">
                        <div className="flex items-center gap-3 font-mono-plex text-xs uppercase tracking-widest text-slate-500">
                            <ChatCircleDots size={16} weight="duotone" />
                            Automated screening
                        </div>
                        <h3 className="font-display text-2xl mt-4">
                            Role-specific questions. Real-time scoring.
                        </h3>
                        <p className="text-slate-600 mt-3 text-sm">
                            An AI chatbot asks questions tailored to each role, scores answers as
                            they arrive, and delivers a fit verdict in minutes — not interview cycles.
                        </p>
                        <div className="mt-6 space-y-2 text-xs">
                            <div className="border border-slate-200 p-2 bg-slate-50">
                                <span className="font-mono-plex text-slate-500">coach ▸</span>{" "}
                                Walk me through how you'd design a rate-limited queue.
                            </div>
                            <div className="border border-slate-200 p-2">
                                <span className="font-mono-plex text-blue-600">you ▸</span>{" "}
                                Token bucket, sliding window, or Redis zset…
                            </div>
                        </div>
                    </div>

                    {/* Video */}
                    <div className="bg-white p-8">
                        <div className="flex items-center gap-3 font-mono-plex text-xs uppercase tracking-widest text-slate-500">
                            <VideoCamera size={16} weight="duotone" />
                            Video intelligence
                        </div>
                        <h3 className="font-display text-2xl mt-4">
                            Communication, confidence, emotional cues.
                        </h3>
                        <p className="text-slate-600 mt-3 text-sm">
                            Candidates opt in. Whisper transcribes, our model reads communication
                            style, confidence and emotional cues — engagement, warmth, composure.
                        </p>
                        <div className="mt-5 grid grid-cols-3 gap-1 text-center">
                            {[
                                { label: "Clarity", v: 86 },
                                { label: "Confidence", v: 72 },
                                { label: "Engagement", v: 91 },
                            ].map((m) => (
                                <div key={m.label} className="border border-slate-200 p-2">
                                    <div className="font-display text-xl">{m.v}</div>
                                    <div className="text-[10px] font-mono-plex uppercase tracking-widest text-slate-500">
                                        {m.label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* DUAL PORTAL */}
            <section className="bg-slate-100 border-y border-slate-200">
                <div className="max-w-[1400px] mx-auto px-6 py-24 grid md:grid-cols-2 gap-px bg-slate-200">
                    <div className="bg-white p-10">
                        <div className="font-mono-plex text-xs uppercase tracking-[0.3em] text-blue-600 mb-3">
                            02 — For job seekers
                        </div>
                        <h3 className="font-display text-4xl leading-tight">
                            Your résumé finally speaks the language of the role.
                        </h3>
                        <ul className="mt-6 space-y-2 text-slate-700">
                            {[
                                "Upload a résumé, get instant role-fit scores",
                                "Practice with an AI skill coach tailored to your stack",
                                "Record a video interview. See what recruiters will see.",
                            ].map((t) => (
                                <li key={t} className="flex gap-3">
                                    <CheckCircle size={20} weight="duotone" className="text-blue-600 mt-0.5 shrink-0" />
                                    <span>{t}</span>
                                </li>
                            ))}
                        </ul>
                        <Link
                            to="/register?role=seeker"
                            data-testid="portal-seeker-cta"
                            className="mt-8 inline-flex items-center gap-2 px-5 py-3 bg-slate-950 text-white hover:bg-blue-600 transition-colors"
                        >
                            Build my profile <ArrowRight size={16} />
                        </Link>
                    </div>
                    <div className="bg-white p-10 relative overflow-hidden">
                        <div className="font-mono-plex text-xs uppercase tracking-[0.3em] text-blue-600 mb-3">
                            03 — For companies
                        </div>
                        <h3 className="font-display text-4xl leading-tight">
                            Post once. Meet your top five by tomorrow.
                        </h3>
                        <ul className="mt-6 space-y-2 text-slate-700">
                            {[
                                "Auto-ranked applicants with explainable match reasoning",
                                "Review video interviews with clarity & sentiment scores",
                                "Move fast: shortlist, reject or schedule — all in one view",
                            ].map((t) => (
                                <li key={t} className="flex gap-3">
                                    <Target size={20} weight="duotone" className="text-blue-600 mt-0.5 shrink-0" />
                                    <span>{t}</span>
                                </li>
                            ))}
                        </ul>
                        <Link
                            to="/register?role=company"
                            data-testid="portal-company-cta"
                            className="mt-8 inline-flex items-center gap-2 px-5 py-3 bg-slate-950 text-white hover:bg-blue-600 transition-colors"
                        >
                            Post a job <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="max-w-[1400px] mx-auto px-6 py-24 text-center">
                <Lightning size={28} className="mx-auto text-blue-600" weight="duotone" />
                <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight">
                    Stop skimming résumés.
                    <br />
                    Start reading talent intelligence.
                </h2>
                <div className="mt-8 inline-flex flex-wrap gap-3 justify-center">
                    <Link
                        to="/register"
                        data-testid="footer-cta-signup"
                        className="px-6 py-3 bg-slate-950 text-white hover:bg-blue-600"
                    >
                        Create free account
                    </Link>
                    <Link
                        to="/jobs"
                        data-testid="footer-cta-browse"
                        className="px-6 py-3 border border-slate-300 hover:bg-slate-950 hover:text-white"
                    >
                        Browse open roles
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    );
}
